import { useState, type FormEvent } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { CheckCircle } from 'lucide-react'
import { authClient } from '@papan/auth/client'
import { useTranslation } from '@papan/i18n'
import { PasswordInput } from '@papan/core-ui/ui'
import { AuthLayout } from '../components/AuthLayout'
import { safeNext } from '../lib/safe-next'

export function ResetPasswordPage() {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  // resetPassword does NOT create a session, so forward ?next= into /login —
  // the user signs in with the new password and lands back at the share.
  const rawNext = searchParams.get('next')
  const loginTarget = rawNext
    ? `/login?next=${encodeURIComponent(safeNext(rawNext))}`
    : '/login'
  const forgotTarget = rawNext
    ? `/forgot-password?next=${encodeURIComponent(safeNext(rawNext))}`
    : '/forgot-password'

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')

    if (password.length < 8) {
      setError(t('signup.passwordTooShort'))
      return
    }
    if (password !== confirmPassword) {
      setError(t('signup.passwordMismatch'))
      return
    }

    setIsLoading(true)

    try {
      const result = await authClient.resetPassword({
        newPassword: password,
        token: token!,
      }) as unknown as { error?: { message?: string } | null }

      if (result.error) {
        setError(result.error.message || t('resetPassword.invalidToken'))
        return
      }

      setSuccess(true)
      setTimeout(() => navigate(loginTarget), 2000)
    } catch {
      setError(t('resetPassword.invalidToken'))
    } finally {
      setIsLoading(false)
    }
  }

  // No token in URL
  if (!token) {
    return (
      <AuthLayout>
        <div className="text-center py-4">
          <p className="text-sm themed-text-secondary mb-4">
            {t('resetPassword.invalidToken')}
          </p>
          <Link
            to={forgotTarget}
            className="text-sm text-blue-400 hover:text-blue-300"
          >
            {t('resetPassword.requestNew')}
          </Link>
        </div>
      </AuthLayout>
    )
  }

  // Success state
  if (success) {
    return (
      <AuthLayout>
        <div className="text-center py-4">
          <CheckCircle size={40} className="mx-auto mb-4 text-green-400" />
          <h2 className="text-lg font-semibold themed-text mb-2">
            {t('resetPassword.success')}
          </h2>
          <p className="text-sm themed-text-muted mb-2">
            {t('resetPassword.signedOutEverywhere')}
          </p>
          <p className="text-sm themed-text-muted">
            {t('resetPassword.redirecting')}
          </p>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 text-sm text-red-400 bg-red-900/20 border border-red-800 rounded">
            {error}
          </div>
        )}

        <div>
          <label htmlFor="password" className="block text-sm themed-text-secondary mb-1">
            {t('resetPassword.newPassword')}
          </label>
          {/* `new-password` on BOTH fields. Without it the browser reads the
              pair as a login form and can autofill the SAVED password — which
              on THIS page would silently reset the password back to the old
              one, filling the confirm field to match so the mismatch check
              never fires. People land here precisely because that password was
              forgotten or leaked. */}
          <PasswordInput
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full themed-input border rounded-md px-3 py-2 outline-none focus:border-blue-500"
            required
            autoFocus
            autoComplete="new-password"
            minLength={8}
          />
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm themed-text-secondary mb-1">
            {t('resetPassword.confirmPassword')}
          </label>
          <PasswordInput
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full themed-input border rounded-md px-3 py-2 outline-none focus:border-blue-500"
            required
            autoComplete="new-password"
            minLength={8}
          />
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg font-medium text-sm transition-colors"
        >
          {isLoading ? t('resetPassword.submitting') : t('resetPassword.submit')}
        </button>
      </form>
    </AuthLayout>
  )
}
